import api from './api';
import { PaginationResult } from './pagination';

export type AtivacaoContratoStatus =
  | 'Pendente'
  | 'EmProcessamento'
  | 'Sucesso'
  | 'Divergencia'
  | 'PendenteAcaoManual'
  | 'ErroTecnico'
  | 'NaoProcessado'
  | 'AguardandoAtivacao'
  | 'AtivadoManualmente';

export type AtivacaoContratoResultadoStatus = 'Sucesso' | 'Divergencia' | 'Falha' | 'NaoExecutado';

export type AtivacaoContratoTipoOperacao = 'Validacao' | 'Preenchimento';

export interface AtivacaoContratoResumo {
  id: string;
  contratoId: string;
  contratoNumero: number | null;
  ordemCompraId: string;
  ordemCompraNumero: number | null;
  handoverConfirmadoPor: string | null;
  status: AtivacaoContratoStatus;
  tipoFalha: string | null;
  criadoEm: string | null;
  contratoAtivadoPor: string | null;
  resumo: string | null;
  administradorContratoId: string | null;
  administradorContratoConfiguracaoId: string | null;
  administradorContrato: string | null;
  administradorContratoPosicao: number | null;
}

export interface AtivacaoContratoResultadoDetalhe {
  id: string;
  regraId: string;
  regraNome: string;
  regraCategoria: 'Padrao' | 'Leitura';
  chaveItem: string | null;
  status: AtivacaoContratoResultadoStatus;
  tipoOcorrencia: AtivacaoContratoTipoOperacao;
  campo: string | null;
  valorMinuta: string | null;
  valorSgc: string | null;
  valorAntes: string | null;
  valorDepois: string | null;
  mensagem: string | null;
}

export interface AtivacaoContratoEtapaDetalhe {
  id: string;
  etapaTipoId: string;
  nome: string;
  grupo: 'Minuta' | 'PQ' | 'Ativacao' | 'Email';
  tipo: AtivacaoContratoTipoOperacao;
  status: AtivacaoContratoResultadoStatus;
  mensagem: string | null;
  resultados: AtivacaoContratoResultadoDetalhe[];
}

export interface AtivacaoContratoArquivo {
  id: string;
  nome: string;
  extensao: string | null;
  numero: number | null;
}

export interface AtivacaoContratoDetalhe extends AtivacaoContratoResumo {
  minutaNome: string | null;
  pqNome: string | null;
  minutaArquivoId: string | null;
  pqArquivoId: string | null;
  arquivos: AtivacaoContratoArquivo[];
  etapas: AtivacaoContratoEtapaDetalhe[];
}

export interface AtivacaoContratoFiltros {
  contratoNumero?: number;
  criadoDe?: string;
  criadoAte?: string;
  status?: AtivacaoContratoStatus;
  pagina?: number;
  tamanhoPagina?: number;
}

export interface MinutaTesteExtracaoCampo {
  nome: string;
  valor: string | null;
  confianca: number;
  metodo: string;
}

export interface MinutaTesteExtracaoResultado {
  sucesso: boolean;
  mensagem: string;
  nomeArquivo: string;
  documentoProcessavel: boolean;
  tipoMinuta: string | null;
  confiancaTipoMinuta: number;
  campos: MinutaTesteExtracaoCampo[];
}

export interface PqTesteValidacaoErro {
  codigo: string;
  linha: number | null;
  mensagem: string;
}

export interface PqTesteValidacaoResultado {
  sucesso: boolean;
  valido: boolean;
  mensagem: string;
  nomeArquivo: string;
  valorTotalContrato: number | null;
  valorTotalPq: number | null;
  quantidadeLinhas: number;
  erros: PqTesteValidacaoErro[];
}

export interface AtivacaoContratoTesteUploadResultado {
  sucesso: boolean;
  mensagem: string;
  execucaoId: string | null;
}

export const consultarAtivacoesContrato = (filtros: AtivacaoContratoFiltros) => {
  const query = new URLSearchParams();
  if (filtros.contratoNumero) query.set('contratoNumero', String(filtros.contratoNumero));
  if (filtros.criadoDe) query.set('criadoDe', filtros.criadoDe);
  if (filtros.criadoAte) query.set('criadoAte', filtros.criadoAte);
  if (filtros.status) query.set('status', filtros.status);
  query.set('pagina', String(filtros.pagina || 1));
  query.set('tamanhoPagina', String(filtros.tamanhoPagina || 20));

  return api.get<PaginationResult<AtivacaoContratoResumo>>('ativacaoContrato?' + query.toString());
};

export const obterAtivacaoContrato = (id: string) => api.get<AtivacaoContratoDetalhe>('ativacaoContrato/' + id);

export const verificarTesteExtracaoMinutaDisponivel = () =>
  api.get<{ disponivel: boolean }>('ativacaoContrato/teste/minuta/disponivel');

export const verificarHandoverTesteDisponivel = () =>
  api.get<{ disponivel: boolean }>('ativacaoContrato/teste/disponivel');

export const testarExtracaoMinuta = (minuta: File) => {
  const form = new FormData();
  form.append('minuta', minuta);

  return api.post<MinutaTesteExtracaoResultado>('ativacaoContrato/teste/minuta', form, {
    disableNotification: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const verificarTestePqDisponivel = () =>
  api.get<{ disponivel: boolean }>('ativacaoContrato/teste/pq/disponivel');

export const executarHandoverTeste = (
  numeroContrato: string,
  minuta: File,
  pq: File,
  isMultiplosGestores: boolean,
  isFaturamentoDireto: boolean,
  isAdiantamentoFinanceiro: boolean,
) => {
  const form = new FormData();
  form.append('numeroContrato', numeroContrato);
  form.append('minuta', minuta);
  form.append('pq', pq);
  form.append('isMultiplosGestores', String(isMultiplosGestores));
  form.append('isFaturamentoDireto', String(isFaturamentoDireto));
  form.append('isAdiantamentoFinanceiro', String(isAdiantamentoFinanceiro));

  return api.post<AtivacaoContratoTesteUploadResultado>('ativacaoContrato/teste', form, {
    disableNotification: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const testarValidacaoPq = (pq: File, valorTotalContrato: string) => {
  const form = new FormData();
  form.append('pq', pq);
  form.append('valorTotalContrato', valorTotalContrato);

  return api.post<PqTesteValidacaoResultado>('ativacaoContrato/teste/pq', form, {
    disableNotification: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
